package com.partejar.app

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.compose.rememberNavController
import androidx.savedstate.read
import com.partejar.app.core.navigation.AppRoutes
import com.partejar.app.core.navigation.appRoute
import com.partejar.app.core.session.SessionDestination
import com.partejar.app.core.security.AppLockCoordinator
import com.partejar.app.core.security.rememberBiometricAuthenticator
import com.partejar.app.core.security.presentation.AppLockHost
import com.partejar.app.core.ui.components.PartejarDockDestination
import com.partejar.app.core.ui.components.PartejarFloatingDock
import com.partejar.app.core.ui.components.PartejarLayout
import com.partejar.app.core.ui.components.PartejarResponsive
import com.partejar.app.core.ui.components.PartejarWindowSize
import com.partejar.app.core.ui.components.partejarWindowSize
import com.partejar.app.core.ui.theme.AppThemeViewModel
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.feature.auth.presentation.AuthScreen
import com.partejar.app.feature.account.data.AccountDeletionReceipt
import com.partejar.app.feature.account.presentation.AccountDeletionScreen
import com.partejar.app.feature.account.presentation.AccountDeletionConfirmationScreen
import com.partejar.app.feature.agenda.data.AgendaPatientDestination
import com.partejar.app.feature.agenda.presentation.AgendaScreen
import com.partejar.app.feature.calendar.presentation.CalendarIntegrationScreen
import com.partejar.app.feature.contratos.presentation.ContratoScreen
import com.partejar.app.feature.contratos.presentation.PublicContractSignatureScreen
import com.partejar.app.feature.convite.presentation.ConviteAceiteScreen
import com.partejar.app.feature.convite.presentation.ConviteLinkParser
import com.partejar.app.feature.equipe.presentation.EquipeScreen
import com.partejar.app.feature.gestantes.presentation.GestantesScreen
import com.partejar.app.feature.gestantes.presentation.GestanteComorbiditiesScreen
import com.partejar.app.feature.gestantes.presentation.GestanteDetailScreen
import com.partejar.app.feature.gestantes.presentation.GestanteExamsScreen
import com.partejar.app.feature.home.presentation.HomeScreen
import com.partejar.app.feature.modeloscontrato.presentation.ModelosContratoScreen
import com.partejar.app.feature.planos.presentation.PlanosScreen
import com.partejar.app.feature.profile.presentation.ProfileScreen
import com.partejar.app.feature.relacionamentos.presentation.NovoRelacionamentoScreen
import com.partejar.app.feature.relacionamentos.presentation.RelacionamentoDetailScreen
import com.partejar.app.feature.relacionamentos.presentation.RelacionamentoEditScreen
import com.partejar.app.feature.relacionamentos.presentation.RelacionamentosScreen
import com.partejar.app.feature.settings.presentation.SettingsScreen
import com.partejar.app.feature.splash.presentation.SplashScreen
import com.partejar.app.feature.workspace.presentation.CreateWorkspaceScreen
import com.partejar.app.feature.workspace.presentation.EditWorkspaceScreen
import com.partejar.app.feature.workspace.presentation.FirstWorkspaceScreen
import com.partejar.app.feature.workspace.presentation.ManageWorkspacesScreen
import com.partejar.app.feature.workspace.presentation.WorkspaceSelectionScreen
import org.koin.compose.KoinContext
import org.koin.compose.koinInject

@Composable
fun App(
    inviteLink: String? = null,
    publicSignatureToken: String? = null,
    startAccountDeletion: Boolean = false
) {
    KoinContext {
        val themeViewModel: AppThemeViewModel = koinInject()
        val appLockCoordinator: AppLockCoordinator = koinInject()
        val biometricAuthenticator = rememberBiometricAuthenticator()
        val themeMode by themeViewModel.themeMode.collectAsState()
        val systemInDarkTheme = isSystemInDarkTheme()

        val isDarkTheme = when (themeMode) {
            ThemeMode.SYSTEM -> systemInDarkTheme
            ThemeMode.LIGHT -> false
            ThemeMode.DARK -> true
        }

        PartejarTheme(themeMode = themeMode) {
            Surface(
                modifier = Modifier.fillMaxSize(),
                color = MaterialTheme.colorScheme.background
            ) {
                PartejarResponsive(Modifier.fillMaxSize()) {
                publicSignatureToken?.let { token ->
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
                        Box(Modifier.widthIn(max = PartejarLayout.readingMaxWidth).fillMaxSize()) {
                            PublicContractSignatureScreen(token)
                        }
                    }
                } ?: AppLockHost(appLockCoordinator, biometricAuthenticator, isDarkTheme) {
                    val navController = rememberNavController()
                    val navBackStackEntry by navController.currentBackStackEntryAsState()
                    val currentRoute = navBackStackEntry?.destination?.route

                    var editingWorkspaceId by remember { mutableStateOf<Long?>(null) }
                    var createReturnsToHome by remember { mutableStateOf(false) }
                    var pendingInviteToken by remember { mutableStateOf(ConviteLinkParser.tokenFrom(inviteLink.orEmpty())) }
                    var selectedRelationshipId by remember { mutableStateOf<Long?>(null) }
                    var relationshipDetailRevision by remember { mutableIntStateOf(0) }
                    var selectedGestanteId by remember { mutableStateOf<Long?>(null) }
                    var selectedContractPatientId by remember { mutableStateOf<Long?>(null) }
                    var pendingDeletionNavigation by remember { mutableStateOf(startAccountDeletion) }
                    var accountDeletionReceipt by remember { mutableStateOf<AccountDeletionReceipt?>(null) }

                    val dockRoutes = listOf(
                        AppRoutes.HOME,
                        AppRoutes.GESTANTES,
                        AppRoutes.RELACIONAMENTOS,
                        AppRoutes.AGENDA,
                        AppRoutes.SETTINGS
                    )

                    val currentDockDestination = when (currentRoute) {
                        AppRoutes.HOME -> PartejarDockDestination.HOME
                        AppRoutes.GESTANTES -> PartejarDockDestination.GESTANTES
                        AppRoutes.RELACIONAMENTOS -> PartejarDockDestination.RELACIONAMENTOS
                        AppRoutes.AGENDA -> PartejarDockDestination.AGENDA
                        AppRoutes.SETTINGS -> PartejarDockDestination.CONFIGURACOES
                        else -> null
                    }
                    val contentMaxWidth = when (currentRoute) {
                        AppRoutes.HOME -> PartejarLayout.contentMaxWidth
                        AppRoutes.GESTANTE_DETAIL -> PartejarLayout.contentMaxWidth
                        AppRoutes.GESTANTES, AppRoutes.RELACIONAMENTOS,
                        AppRoutes.AGENDA, AppRoutes.SETTINGS -> PartejarLayout.readingMaxWidth
                        AppRoutes.AUTH, AppRoutes.AUTH_INVITE,
                        AppRoutes.FIRST_WORKSPACE, AppRoutes.CREATE_WORKSPACE,
                        AppRoutes.EDIT_WORKSPACE, AppRoutes.RELACIONAMENTO_NEW,
                        AppRoutes.RELACIONAMENTO_EDIT -> PartejarLayout.formMaxWidth
                        else -> PartejarLayout.readingMaxWidth
                    }

                    val navigateToResolvedDestination: (SessionDestination) -> Unit = { destination ->
                        val route = if (pendingDeletionNavigation) {
                            pendingDeletionNavigation = false
                            AppRoutes.ACCOUNT_DELETION
                        } else when (destination) {
                            SessionDestination.HOME -> AppRoutes.HOME
                            SessionDestination.WORKSPACE_SELECTION -> AppRoutes.WORKSPACE_SELECTION
                            SessionDestination.FIRST_WORKSPACE -> AppRoutes.FIRST_WORKSPACE
                        }
                        navController.navigate(route) {
                            popUpTo(0) { inclusive = true }
                            launchSingleTop = true
                        }
                    }

                    val onDockDestination: (PartejarDockDestination) -> Unit = { destination ->
                        destination.appRoute()?.let { route ->
                            navController.navigate(route) {
                                popUpTo(AppRoutes.HOME) { saveState = true }
                                launchSingleTop = true
                                restoreState = true
                            }
                        }
                    }

                    Box(modifier = Modifier.fillMaxSize()) {
                        NavHost(
                            navController = navController,
                            modifier = Modifier.align(Alignment.TopCenter)
                                .then(
                                    if (currentRoute == AppRoutes.HOME) {
                                        Modifier
                                    } else {
                                        Modifier.widthIn(max = contentMaxWidth)
                                    }
                                )
                                .fillMaxSize(),
                            startDestination = if (inviteLink != null) AppRoutes.INVITE else AppRoutes.SPLASH,
                            enterTransition = {
                                if (initialState.destination.route in dockRoutes && targetState.destination.route in dockRoutes) {
                                    fadeIn(animationSpec = tween(durationMillis = 250))
                                } else {
                                    slideInHorizontally(
                                        initialOffsetX = { (it * 0.2f).toInt() },
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    ) + fadeIn(
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    )
                                }
                            },
                            exitTransition = {
                                if (initialState.destination.route in dockRoutes && targetState.destination.route in dockRoutes) {
                                    fadeOut(animationSpec = tween(durationMillis = 250))
                                } else {
                                    slideOutHorizontally(
                                        targetOffsetX = { -(it * 0.1f).toInt() },
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    ) + fadeOut(
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    )
                                }
                            },
                            popEnterTransition = {
                                if (initialState.destination.route in dockRoutes && targetState.destination.route in dockRoutes) {
                                    fadeIn(animationSpec = tween(durationMillis = 250))
                                } else {
                                    slideInHorizontally(
                                        initialOffsetX = { -(it * 0.1f).toInt() },
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    ) + fadeIn(
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    )
                                }
                            },
                            popExitTransition = {
                                if (initialState.destination.route in dockRoutes && targetState.destination.route in dockRoutes) {
                                    fadeOut(animationSpec = tween(durationMillis = 250))
                                } else {
                                    slideOutHorizontally(
                                        targetOffsetX = { (it * 0.2f).toInt() },
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    ) + fadeOut(
                                        animationSpec = tween(durationMillis = 350, easing = FastOutSlowInEasing)
                                    )
                                }
                            }
                        ) {
                            composable(AppRoutes.SPLASH) {
                                SplashScreen(
                                    isDarkTheme = isDarkTheme,
                                    onNavigateToAuth = {
                                        navController.navigate(AppRoutes.AUTH) {
                                            popUpTo(AppRoutes.SPLASH) { inclusive = true }
                                        }
                                    },
                                    onDestinationResolved = navigateToResolvedDestination
                                )
                            }

                            composable(AppRoutes.AUTH) {
                                AuthScreen(
                                    isDarkTheme = isDarkTheme,
                                    onThemeToggle = {
                                        themeViewModel.toggleManualTheme(isDarkTheme)
                                    },
                                    onDestinationResolved = navigateToResolvedDestination
                                )
                            }

                            composable(AppRoutes.AUTH_INVITE) {
                                AuthScreen(
                                    isDarkTheme = isDarkTheme,
                                    onThemeToggle = {
                                        themeViewModel.toggleManualTheme(isDarkTheme)
                                    },
                                    onDestinationResolved = navigateToResolvedDestination,
                                    onAuthenticated = {
                                        navController.navigate(AppRoutes.INVITE) {
                                            popUpTo(AppRoutes.AUTH_INVITE) { inclusive = true }
                                        }
                                    }
                                )
                            }

                            composable(AppRoutes.INVITE) {
                                ConviteAceiteScreen(
                                    token = pendingInviteToken.orEmpty(),
                                    onBack = { navController.popBackStack() },
                                    onSignIn = { navController.navigate(AppRoutes.AUTH_INVITE) },
                                    onDestinationResolved = { destination ->
                                        pendingInviteToken = null
                                        navigateToResolvedDestination(destination)
                                    }
                                )
                            }

                            composable(AppRoutes.HOME) {
                                HomeScreen(
                                    isDarkTheme = isDarkTheme,
                                    onNewRelationship = { navController.navigate(AppRoutes.RELACIONAMENTO_NEW) },
                                    onThemeToggle = { themeViewModel.toggleManualTheme(isDarkTheme) },
                                    onDestinationSelected = onDockDestination,
                                    onAttentionSelected = {
                                        onDockDestination(PartejarDockDestination.RELACIONAMENTOS)
                                    },
                                    onGestanteSelected = { id ->
                                        selectedGestanteId = id.toLongOrNull()
                                        selectedGestanteId?.let {
                                            navController.navigate(AppRoutes.GESTANTE_DETAIL)
                                        }
                                    }
                                )
                            }

                            composable(AppRoutes.GESTANTES) {
                                GestantesScreen(
                                    isDarkTheme = isDarkTheme,
                                    onThemeToggle = { themeViewModel.toggleManualTheme(isDarkTheme) },
                                    onDestinationSelected = onDockDestination,
                                    onOpenDetail = { id ->
                                        selectedGestanteId = id
                                        navController.navigate(AppRoutes.GESTANTE_DETAIL)
                                    }
                                )
                            }

                            composable(AppRoutes.GESTANTE_DETAIL) {
                                selectedGestanteId?.let { patientId ->
                                    GestanteDetailScreen(
                                        patientId = patientId,
                                        onBack = { navController.popBackStack() },
                                        onOpenComorbidities = {
                                            navController.navigate(AppRoutes.gestanteComorbidities(patientId))
                                        },
                                        onOpenExams = {
                                            navController.navigate(AppRoutes.gestanteExams(patientId))
                                        }
                                    )
                                }
                            }

                            composable(AppRoutes.GESTANTE_COMORBIDITIES) { backStackEntry ->
                                backStackEntry.arguments
                                    ?.read { getString(AppRoutes.GESTANTE_PATIENT_ID_ARGUMENT) }
                                    ?.toLongOrNull()
                                    ?.let { patientId ->
                                        GestanteComorbiditiesScreen(
                                            patientId = patientId,
                                            onBack = { navController.popBackStack() }
                                        )
                                    }
                            }

                            composable(AppRoutes.GESTANTE_EXAMS) { backStackEntry ->
                                backStackEntry.arguments
                                    ?.read { getString(AppRoutes.GESTANTE_PATIENT_ID_ARGUMENT) }
                                    ?.toLongOrNull()
                                    ?.let { patientId ->
                                        GestanteExamsScreen(
                                            patientId = patientId,
                                            onBack = { navController.popBackStack() }
                                        )
                                    }
                            }

                            composable(AppRoutes.RELACIONAMENTOS) {
                                RelacionamentosScreen(
                                    isDarkTheme = isDarkTheme,
                                    onThemeToggle = { themeViewModel.toggleManualTheme(isDarkTheme) },
                                    onDestinationSelected = onDockDestination,
                                    onOpenDetail = { id ->
                                        selectedRelationshipId = id
                                        navController.navigate(AppRoutes.RELACIONAMENTO_DETAIL)
                                    },
                                    onNew = { navController.navigate(AppRoutes.RELACIONAMENTO_NEW) }
                                )
                            }

                            composable(AppRoutes.AGENDA) {
                                AgendaScreen(
                                    isDarkTheme = isDarkTheme,
                                    onThemeToggle = { themeViewModel.toggleManualTheme(isDarkTheme) },
                                    onDestinationSelected = onDockDestination,
                                    onPatientSelected = { patientId, destination ->
                                        when (destination) {
                                            AgendaPatientDestination.GESTANTE -> {
                                                selectedGestanteId = patientId
                                                navController.navigate(AppRoutes.GESTANTE_DETAIL)
                                            }
                                            AgendaPatientDestination.RELACIONAMENTO -> {
                                                selectedRelationshipId = patientId
                                                navController.navigate(AppRoutes.RELACIONAMENTO_DETAIL)
                                            }
                                        }
                                    }
                                )
                            }

                            composable(AppRoutes.RELACIONAMENTO_DETAIL) {
                                selectedRelationshipId?.let { id ->
                                    RelacionamentoDetailScreen(
                                        id,
                                        onBack = { navController.popBackStack() },
                                        onEditPerson = { navController.navigate(AppRoutes.RELACIONAMENTO_EDIT) },
                                        reloadKey = relationshipDetailRevision,
                                        onStartContract = { patientId ->
                                            selectedContractPatientId = patientId
                                            navController.navigate(AppRoutes.CONTRACT)
                                        },
                                        onCreatePlan = { navController.navigate(AppRoutes.CREATE_PLAN) },
                                        onCreateContractTemplate = { navController.navigate(AppRoutes.CREATE_CONTRACT_TEMPLATE) }
                                    )
                                }
                            }

                            composable(AppRoutes.RELACIONAMENTO_EDIT) {
                                selectedRelationshipId?.let { id ->
                                    RelacionamentoEditScreen(
                                        id = id,
                                        onBack = { navController.popBackStack() },
                                        onSaved = {
                                            relationshipDetailRevision++
                                            navController.popBackStack()
                                        }
                                    )
                                }
                            }

                            composable(AppRoutes.RELACIONAMENTO_NEW) {
                                NovoRelacionamentoScreen(
                                    onBack = { navController.popBackStack() },
                                    onCreated = { id ->
                                        selectedRelationshipId = id
                                        navController.navigate(AppRoutes.RELACIONAMENTO_DETAIL) {
                                            popUpTo(AppRoutes.RELACIONAMENTO_NEW) { inclusive = true }
                                        }
                                    }
                                )
                            }

                            composable(AppRoutes.WORKSPACE_SELECTION) {
                                WorkspaceSelectionScreen(
                                    onSelected = { navigateToResolvedDestination(SessionDestination.HOME) },
                                    onCreate = {
                                        createReturnsToHome = true
                                        navController.navigate(AppRoutes.CREATE_WORKSPACE)
                                    }
                                )
                            }

                            composable(AppRoutes.FIRST_WORKSPACE) {
                                FirstWorkspaceScreen(
                                    onCreated = {
                                        appLockCoordinator.onSessionAuthenticated()
                                        navigateToResolvedDestination(SessionDestination.HOME)
                                    }
                                )
                            }

                            composable(AppRoutes.PROFILE) {
                                ProfileScreen(onBack = { navController.popBackStack() })
                            }

                            composable(AppRoutes.SETTINGS) {
                                SettingsScreen(
                                    onDestinationSelected = onDockDestination,
                                    onProfile = { navController.navigate(AppRoutes.PROFILE) },
                                    onCurrentWorkspace = { navController.navigate(AppRoutes.TEAM) },
                                    onPlans = { navController.navigate(AppRoutes.PLANS) },
                                    onContractTemplates = { navController.navigate(AppRoutes.CONTRACT_TEMPLATES) },
                                    onCalendar = { navController.navigate(AppRoutes.CALENDAR) },
                                    onDeleteAccount = { navController.navigate(AppRoutes.ACCOUNT_DELETION) },
                                    onLogout = {
                                        navController.navigate(AppRoutes.AUTH) {
                                            popUpTo(0) { inclusive = true }
                                            launchSingleTop = true
                                        }
                                    }
                                )
                            }

                            composable(AppRoutes.ACCOUNT_DELETION) {
                                AccountDeletionScreen(
                                    onBack = { navController.popBackStack() },
                                    onSuccess = { receipt ->
                                        accountDeletionReceipt = receipt
                                        navController.navigate(AppRoutes.ACCOUNT_DELETION_CONFIRMATION) {
                                            popUpTo(0) { inclusive = true }
                                            launchSingleTop = true
                                        }
                                    }
                                )
                            }

                            composable(AppRoutes.ACCOUNT_DELETION_CONFIRMATION) {
                                accountDeletionReceipt?.let { receipt ->
                                    AccountDeletionConfirmationScreen(
                                        alreadyRequested = receipt.alreadyRequested,
                                        requiresWorkspaceResolution = receipt.requiresWorkspaceResolution,
                                        onLogin = {
                                            navController.navigate(AppRoutes.AUTH) {
                                                popUpTo(0) { inclusive = true }
                                                launchSingleTop = true
                                            }
                                        }
                                    )
                                }
                            }

                            composable(AppRoutes.TEAM) {
                                EquipeScreen(
                                    onBack = { navController.popBackStack() },
                                    onWorkspaces = { navController.navigate(AppRoutes.WORKSPACES) },
                                    onEditWorkspace = { workspaceId ->
                                        editingWorkspaceId = workspaceId
                                        navController.navigate(AppRoutes.EDIT_WORKSPACE)
                                    },
                                    onSessionDestination = navigateToResolvedDestination
                                )
                            }

                            composable(AppRoutes.PLANS) {
                                PlanosScreen(onBack = { navController.popBackStack() })
                            }

                            composable(AppRoutes.CONTRACT_TEMPLATES) {
                                ModelosContratoScreen(onBack = { navController.popBackStack() })
                            }
                            composable(AppRoutes.CREATE_PLAN) {
                                PlanosScreen(onBack = { relationshipDetailRevision++; navController.popBackStack() }, startCreating = true)
                            }
                            composable(AppRoutes.CREATE_CONTRACT_TEMPLATE) {
                                ModelosContratoScreen(onBack = { relationshipDetailRevision++; navController.popBackStack() }, startCreating = true)
                            }

                            composable(AppRoutes.CONTRACT) {
                                selectedContractPatientId?.let { patientId ->
                                    ContratoScreen(
                                        patientId = patientId,
                                        onBack = { navController.popBackStack() },
                                        onOpenPatientAfterCompletion = {
                                            selectedGestanteId = patientId
                                            selectedRelationshipId = null
                                            selectedContractPatientId = null
                                            navController.navigate(AppRoutes.GESTANTES) {
                                                popUpTo(AppRoutes.HOME)
                                                launchSingleTop = true
                                            }
                                            navController.navigate(AppRoutes.GESTANTE_DETAIL)
                                        },
                                        onEditPatient = { navController.navigate(AppRoutes.RELACIONAMENTO_EDIT) },
                                        reloadKey = relationshipDetailRevision,
                                        onCreatePlan = { navController.navigate(AppRoutes.CREATE_PLAN) },
                                        onCreateContractTemplate = { navController.navigate(AppRoutes.CREATE_CONTRACT_TEMPLATE) }
                                    )
                                }
                            }

                            composable(AppRoutes.CALENDAR) {
                                CalendarIntegrationScreen(onBack = { navController.popBackStack() })
                            }

                            composable(AppRoutes.WORKSPACES) {
                                ManageWorkspacesScreen(
                                    onBack = { navController.popBackStack() },
                                    onSelected = { navigateToResolvedDestination(SessionDestination.HOME) },
                                    onCreate = {
                                        createReturnsToHome = false
                                        navController.navigate(AppRoutes.CREATE_WORKSPACE)
                                    }
                                )
                            }

                            composable(AppRoutes.CREATE_WORKSPACE) {
                                CreateWorkspaceScreen(
                                    onBack = { navController.popBackStack() },
                                    onCreated = {
                                        if (createReturnsToHome) {
                                            navigateToResolvedDestination(SessionDestination.HOME)
                                        } else {
                                            navController.popBackStack()
                                        }
                                    }
                                )
                            }

                            composable(AppRoutes.EDIT_WORKSPACE) {
                                editingWorkspaceId?.let { workspaceId ->
                                    EditWorkspaceScreen(
                                        workspaceId = workspaceId,
                                        onBack = { navController.popBackStack() },
                                        onUpdated = { navController.popBackStack() }
                                    )
                                }
                            }
                        }

                        if (partejarWindowSize == PartejarWindowSize.EXPANDED) {
                            AnimatedVisibility(
                                visible = currentDockDestination != null,
                                enter = fadeIn(animationSpec = tween(240)) + slideInVertically(
                                    initialOffsetY = { it / 2 },
                                    animationSpec = tween(240, easing = FastOutSlowInEasing)
                                ),
                                exit = fadeOut(animationSpec = tween(200)) + slideOutVertically(
                                    targetOffsetY = { it / 2 },
                                    animationSpec = tween(200, easing = FastOutSlowInEasing)
                                ),
                                modifier = Modifier.align(Alignment.BottomCenter)
                            ) {
                                currentDockDestination?.let { selectedDestination ->
                                    Box(
                                        modifier = Modifier
                                            .widthIn(max = PartejarLayout.dockMaxWidth)
                                            .fillMaxWidth()
                                            .navigationBarsPadding()
                                            .padding(
                                                bottom = PartejarLayout.desktopDockBottomMargin
                                            )
                                    ) {
                                        PartejarFloatingDock(
                                            selectedDestination = selectedDestination,
                                            onDestinationSelected = onDockDestination,
                                            desktop = true
                                        )
                                    }
                                }
                            }
                        } else {
                            AnimatedVisibility(
                                visible = currentDockDestination != null,
                                enter = fadeIn(animationSpec = tween(300)) + slideInVertically(
                                    initialOffsetY = { it },
                                    animationSpec = tween(300, easing = FastOutSlowInEasing)
                                ),
                                exit = fadeOut(animationSpec = tween(300)) + slideOutVertically(
                                    targetOffsetY = { it },
                                    animationSpec = tween(300, easing = FastOutSlowInEasing)
                                ),
                                modifier = Modifier.align(Alignment.BottomCenter)
                            ) {
                                currentDockDestination?.let { selectedDestination ->
                                    Box(
                                        modifier = Modifier
                                            .widthIn(max = PartejarLayout.dockMaxWidth)
                                            .fillMaxWidth()
                                            .navigationBarsPadding()
                                            .padding(
                                                start = PartejarTheme.spacing.space16,
                                                end = PartejarTheme.spacing.space16,
                                                bottom = PartejarTheme.spacing.space12
                                            )
                                    ) {
                                        PartejarFloatingDock(
                                            selectedDestination = selectedDestination,
                                            onDestinationSelected = onDockDestination
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
                }
            }
        }
    }
}
